import { useState, useEffect } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';

// Peacock components
import { TabsNavigation, Tab } from 'pepsico-ds';

import { EntityViewHeader } from '@/components/common/EntityViewHeader/EntityViewHeader';

// Constants
import { ROUTES } from '@/utils/constants/routesConstants';
// Import the useStoreData hook
import { useStoreData } from '@/services/Store/storeService';

const storeInfo = {
  storeInformation: {
    storeId: '101010101',
    platformType: 'Mobile',
    lastActiveDate: '2023-09-15T14:30:45Z',
    storeRegistrationDate: '2021-08-20T09:15:22Z',
    address: {
      addressLine1: '123 Main St',
      addressLine2: 'Suite 456',
      city: 'Metropolis',
      stateOrProvince: 'NY',
      postalCode: '10001',
      country: 'USA',
      countryCode: 'US',
      region: 'North America'
    },
    currentPoints: 995,
    billingType: 'SoldTo',
    rewardsEngineApplicationUserId: '90909090',
    deletedRewardPoints: 10,
    clubPlus: false
  },
  customerInformation: {
    customerId: '1234567890',
    customerUserId: '12123321@pepsiconnect.com',
    customerName: 'John Smith',
    loginName: 'johnsmith',
    customerType: 'Sold to',
    paceId: '1234567890'
  },
  deliveryData: [
    {
      localizationId: 'LOC-1001',
      routeId: 'MX1B45',
      routeType: '1',
      moa: '100',
      deliveryService: '24',
      cutoffTime: '18:00',
      deliveryDays: ['Monday', 'Wednesday', 'Friday']
    },
    {
      localizationId: 'LOC-2002',
      routeId: 'MX2C78',
      routeType: '2',
      moa: '150',
      deliveryService: '48',
      cutoffTime: '16:30',
      deliveryDays: ['Tuesday', 'Thursday']
    }
  ],
  segments: [
    {
      segmentName: 'High Value Customers',
      description: 'Customers with high purchase frequency and volume',
      creationDate: '2022-01-15'
    },
    {
      segmentName: 'Loyalty Program Members',
      description: 'Customers enrolled in the rewards program',
      creationDate: '2022-03-22'
    },
    {
      segmentName: 'New Customers',
      description: 'Customers who joined in the last 90 days',
      creationDate: '2022-06-10'
    },
    {
      segmentName: 'Beverage Enthusiasts',
      description: 'Customers who primarily purchase beverage products',
      creationDate: '2022-08-05'
    },
    {
      segmentName: 'Snack Lovers',
      description: 'Customers who primarily purchase snack products',
      creationDate: '2022-09-18'
    }
  ],
  salesInformation: {
    salesOfficeCode: 'MXCT',
    salesOfficeName: 'Mexico City Office',
    salesChannelCode: 'Z3',
    salesOrganization: 'MX21',
    salesBlockStatus: 'None',
    salesVolumeClassificationValue: '150000',
    relationshipTypeCode: '0004',
    businessType: ['Snacks', 'Beverages'],
    businessSegmentationNodeCode: 'Example',
    businessModelCode: 'Example',
    classificationCode: 'KVGR5',
    classificationTypeCode: 'KVGR5',
    moa: '100',
    gtmu: '10',
    division: '0'
  },
  payment: {
    paymentMethodCode: 'E',
    currency: 'MXN',
    creditBlock: 'N',
    creditLimitAmount: '-',
    paymentTermCode: 'Z000',
    priceGroup: '17',
    priceList: '0'
  },
  tax: {
    taxAreaCode: 'TR',
    vatNumber: '1234567890',
    vatRegistrationDate: '2020-12-01T00:00:00Z',
    taxType: '0',
    taxId: '1234567890',
    sugarTax: true,
    isTestUser: false
  }
};

export const StorePage = () => {
  // Access the storeId parameter from the URL
  const { storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Use the useStoreData hook to fetch store data
  const { data: storeData, isLoading, error } = useStoreData(storeId);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Effect to sync the selected tab with the current URL path
  useEffect(() => {
    const path = location.pathname;
    // Check for the payment and tax route first because it contains the profile route substring
    if (
      path.includes(
        ROUTES.STORE_VIEW_PAYMENT_AND_TAX.replace(':storeId', storeId)
      )
    ) {
      setSelectedTabIndex(1); // Payment and Tax tab
    } else if (path.includes(`/profile/store/${storeId}`)) {
      setSelectedTabIndex(0); // Profile tab
    }
  }, [location.pathname, storeId]); // Re-run when URL or storeId changes

  // Handler for tab click events
  const handleTabClick = index => {
    // Update the selected tab state
    setSelectedTabIndex(index);
    // Navigate to the corresponding route based on tab index
    switch (index) {
      case 0:
        navigate(ROUTES.STORE_VIEW.replace(':storeId', storeId)); // Profile
        break;
      case 1:
        navigate(
          ROUTES.STORE_VIEW_PAYMENT_AND_TAX.replace(':storeId', storeId)
        ); // Payment and Tax
        break;
      default:
        break;
    }
  };

  // Example breadcrumbs
  const breadcrumbItems = [
    {
      href: '/',
      label: 'Home'
    },
    {
      href: ROUTES.PROFILE_MANAGEMENT_STORES,
      label: 'Stores'
    },
    {
      href: ROUTES.STORE_VIEW.replace(':storeId', storeId),
      label: `Store ${storeId}`
    }
  ];

  const outletContext = {
    storeId,
    storeData
  };

  // Add loading and error states
  if (isLoading) return <div className='p-6'>Loading store information...</div>;
  if (error)
    return (
      <div className='p-6 text-red-500'>
        Error loading store data: {error.message}
      </div>
    );

  const getTagColor = status => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'SUSPENDED':
        return 'lightgray';
      case 'INACTIVE':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <div>
      <EntityViewHeader
        title={`Store ${storeId}`}
        breadcrumbs={breadcrumbItems}
        tagText={storeData?.customerInformation?.activeStatus}
        tagColor={getTagColor(storeData?.customerInformation?.activeStatus)}
        fields={[
          {
            label: null, // no label for store name (just the value)
            value:
              storeData?.storeInformation?.storeName ||
              'Store Name Unavailable',
            isBold: true // make store name bold
          },
          {
            label: 'Store Type',
            value: storeData?.storeInformation?.platformType
          },
          {
            label: 'Last Login',
            value: storeData?.storeInformation?.lastActiveDate
          },
          {
            label: 'Current Points',
            value: storeData?.storeInformation?.currentPoints
          }
        ]}
      />

      <div className='mb-6 mt-10'>
        <TabsNavigation isDark={true} selectedIndex={selectedTabIndex}>
          <Tab
            className='bg-[#131313]'
            label='Profile'
            onClick={() => handleTabClick(0)}
          />
          <Tab
            className='bg-[#131313]'
            label='Payment and Tax'
            onClick={() => handleTabClick(1)}
          />
        </TabsNavigation>
      </div>

      <div className=''>
        <Outlet context={outletContext} />
      </div>
    </div>
  );
};
